import {
  AppBar,
  Toolbar,
  IconButton,
  Badge,
  Box,
  Menu,
  MenuItem,
  useMediaQuery,
  Drawer,
  Popover,
  Avatar,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import ForecastResultModal from "../forecast-result-modal";
import ForecastParamsModal from "../forecast-params-modal";
import LostSalesParamsModal from "../lost-sales-params-modal";
import LostSalesResultModal from "../lost-sales-result-modal";
import { useNavigate } from "react-router-dom";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import AppContext from "../context/context";
import Sidebar from "../sidebar/sidebar";
import Breadcrumbs from "../breadcrumbs";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import CustomTypography from "../../globalStyles/fonts";
import NotificationModal from "../notificationModal";
import { styled } from "@mui/material/styles";
import AccountSettingsModal from "../account-setting";
import SwitchBrandModal from "../switch-brand-modal";
import { clientService } from "../../utils/services/clientService";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { notificationService } from "../../utils/services/notificationService";
import buildQuery from "../../utils/query-builder";
import { useDarkMode } from "../context/darkModeContext";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";
import { clearDeviceId } from "../../utils/deviceId.util";
import { toast } from "react-toastify";
import wsManager from "../../utils/websocketManager";

const ResponsiveImage = styled("img")({
  width: "100px",
  height: "50px",
  maxWidth: "100%",
  maxHeight: "100%",
});

export default function Layout({ children, title }) {
  const [open, setOpen] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [notifications, setNotifications] = React.useState([]);
  const [notificationCount, setNotificationCount] = React.useState(0);
  const [notificationLoading, setNotificationLoading] = React.useState(false);
  const [activeSection, setActiveSection] = useState("General");
  const [showDrawer, setShowDrawer] = useState(true);
  const [drawerWidth, setDrawerWidth] = useState(215);
  const [anchorNotificationEl, setAnchorNotificationEl] = useState(null);
  const [openAccountSettingModal, setOpenAccountSettingModal] = useState(false);
  const [switchBrandOpen, setSwitchBrandOpen] = useState(false);
  const [clientDetails, setClientDetails] = useState(null);
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [loadMore, setLoadMore] = useState(false);
  const client_id = localStorage.getItem("client_id");
  const { user, setIsAuthenticated, setIsSidebarOpen, isSidebarOpen } =
    React.useContext(AppContext);
  const { darkMode, toggleDarkMode, colors } = useDarkMode();
  const toggleDrawer = () => {
    setOpen(!open);
  };

  const roles = localStorage.getItem("roles")
    ? JSON.parse(localStorage.getItem("roles"))
    : [];

  const hasAdminToken = !!localStorage.getItem("adminToken");

  const availableClients = localStorage.getItem("clients")
    ? JSON.parse(localStorage.getItem("clients"))
    : [];
  const canSwitchBrand =
    availableClients.length > 1 && !!localStorage.getItem("base_token");

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const fullLogout = () => {
    const darkMode = localStorage.getItem("darkMode") === "true";
    clearDeviceId();
    setIsAuthenticated(false);
    navigate("/login", { replace: true });
    localStorage.clear();
    localStorage.setItem("darkMode", darkMode);
  };

  const logout = () => {
    const adminData = localStorage.getItem("adminUser");
    const adminToken = localStorage.getItem("adminToken");
    const adminRoles = localStorage.getItem("admin_roles");
    const darkMode = localStorage.getItem("darkMode") === "true";

    // Clear device ID on logout
    clearDeviceId();

    if (adminData) {
      const token = localStorage.getItem("token");
      localStorage.clear();
      localStorage.setItem("user", adminData);
      localStorage.setItem("token", adminToken);
      localStorage.setItem("roles", adminRoles);
      localStorage.setItem("path", "/");
      localStorage.setItem("darkMode", darkMode);

      window.location.reload();
    } else {
      setIsAuthenticated(false);
      navigate("/login", { replace: true });
      localStorage.clear();
      localStorage.setItem("darkMode", darkMode);
    }
  };

  const handleNotificationClick = async (event) => {
    setAnchorNotificationEl(event.currentTarget);
    fetchNotifications();
  };

  const fetchNotifications = async () => {
    try {
      setNotificationLoading(true);
      const controller = new AbortController();
      const signal = controller.signal;
      let notificationData = await notificationService.fetch(
        client_id,
        buildQuery(
          {
            columns: [
              { key: "notification_status", filter: ["New", "Read"] },
              {
                key: "type",
                filter:
                  activeSection === "General"
                    ? []
                    : ["FORECAST_UPDATE", "FORECAST_CATEGORY_DONE"],
                initExcludeValues:
                  activeSection === "General"
                    ? ["FORECAST_UPDATE", "FORECAST_CATEGORY_DONE"]
                    : [],
              },
            ],
          },
          page,
        ),
        signal,
      );
      setNotifications(
        page
          ? [...notifications, ...notificationData.data]
          : notificationData.data,
      );
      setLoadMore(notificationData.total > (page + 1) * 15);
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
      setNotifications([]);
    } finally {
      setNotificationLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, [activeSection, page]);

  const handleNotificationClose = () => {
    setAnchorNotificationEl(null);
    setNotifications([]);
    setPage(0);
  };
  const fetchClientById = async () => {
    let client = await clientService.get(localStorage.getItem("client_id"));
    setClientDetails(client);
  };

  useEffect(() => {
    if (localStorage.getItem("client_id")) {
      fetchClientById();
    }
    fetchNotificationCount();
  }, [user]);

  // Establish the app-wide WebSocket connection once the user is known — the
  // listeners below (and any other page's wsManager.on(...) subscriptions,
  // e.g. the forecast artifact one) are otherwise never actually reachable
  // unless something else happens to have called wsManager.connect() first.
  useEffect(() => {
    if (user?._id) wsManager.connect(user._id);
  }, [user?._id]);

  useEffect(() => {
    const unsub = wsManager.on("forecastCategoryDone", (payload) => {
      toast.info(
        `Forecast for category "${payload.category}" is complete — ${payload.count} SKU${payload.count !== 1 ? "s" : ""} processed`,
        { autoClose: 8000 },
      );
      fetchNotificationCount();
    });
    return () => {
      if (typeof unsub === "function") unsub();
    };
  }, []);

  const fetchNotificationCount = async () => {
    try {
      const controller = new AbortController();
      const signal = controller.signal;
      let notificationData = await notificationService.fetch(
        client_id,
        buildQuery(
          { columns: [{ key: "notification_status", filter: ["New"] }] },
          0,
        ),
        signal,
      );
      setNotificationCount(notificationData.total || 0);
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
    }
  };

  const isMobile = useMediaQuery("(max-width:700px)");

  const handleSearchData = (searchTerm) => {
    console.log(searchTerm);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <AppBar
        sx={{
          display: "flex",
          width: isMobile
            ? "100vw"
            : drawerWidth === 215
              ? `calc(100% - 215px)`
              : `calc(100% - 80px)`,
          height: 60,
          justifyContent: "center",
          backgroundColor: "var(--bg-primary)",
          color: "var(--bg-secondary)",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          boxShadow: "none", // Removes shadow
          borderLeft: "none",
          borderBottom: `1px solid var(--border-color)`,
          flexDirection: "row",
        }}
        position="fixed"
      >
        {isMobile ? (
          <Box sx={{ position: "relative", marginLeft: 1, marginTop: 1 }}>
            <img
              onClick={() => {
                setOpen(!open);
                setShowDrawer(!showDrawer);
                setIsSidebarOpen(!isSidebarOpen);
              }}
              style={{ height: 40, width: 60 }}
              src={
                clientDetails?.logo
                  ? `${clientDetails?.logo}`
                  : "/DropSkipLogo.png"
              }
              alt="Logo"
            />
          </Box>
        ) : (
          <></>
        )}

        <Toolbar
          sx={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
            backgroundColor: "var(--bg-secondary)",
          }}
        >
          {!isMobile ? (
            <div style={{ display: "flex", textAlign: "left", width: "auto" }}>
              <Breadcrumbs />
            </div>
          ) : (
            <></>
          )}

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              justifyContent: "flex-end",
              width: isMobile ? "100%" : "auto",
            }}
          >
            <IconButton onClick={toggleDarkMode} aria-label="toggle dark mode">
              {darkMode ? <LightModeIcon /> : <DarkModeIcon />}
            </IconButton>
            <Box sx={{ marginRight: isMobile ? 0 : 5 }}>
              <IconButton
                size="large"
                edge="end"
                color="primary"
                aria-label="notifications"
                onClick={handleNotificationClick}
              >
                <Badge badgeContent={notificationCount} color="error">
                  <NotificationsNoneIcon />
                </Badge>
              </IconButton>
            </Box>
            <Avatar
              src={user?.image}
              alt="Profile Image"
              sx={{
                width: 55,
                height: 40,
                borderRadius: 12,
                backgroundColor: "primary.main",
                fontSize: "14px",
                fontWeight: 600,
                color: "#fff",
              }}
            >
              {user?.name
                ? user.name
                    .split(" ")
                    .map((n) => n[0])
                    .join("")
                    .toUpperCase()
                    .slice(0, 2)
                : "U"}
            </Avatar>
            <Box
              onClick={handleMenuClick}
              sx={{ display: "flex", flexDirection: "column" }}
            >
              <CustomTypography
                color="var(--text-primary)"
                fontSize={"13px"}
                fontWeight={500}
              >
                {user?.name}
              </CustomTypography>
              {/* <CustomTypography color="#333333" fontSize={'12px'} fontWeight={400}>
                  {user?.role}
                </CustomTypography> */}
            </Box>
            <Box>
              <IconButton
                size="large"
                edge="end"
                color="primary"
                aria-label="account of current user"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleMenuClick}
              >
                <ArrowDropDownIcon color="#151D48" />
              </IconButton>
              <Menu
                id="menu-appbar"
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                MenuListProps={{
                  sx: isMobile
                    ? {
                        "& .MuiMenuItem-root": {
                          minHeight: 35,
                          py: 1,
                        },
                      }
                    : {},
                }}
              >
                <MenuItem
                  sx={{ color: "var(--text-primary)" }}
                  onClick={() => {
                    localStorage.setItem("path", `/account-setting`);
                    navigate(`/account-setting`);
                    setAnchorEl(null);
                    if (isMobile) {
                      setOpen(false);
                    }
                  }}
                >
                  <CustomTypography
                    color="var(--text-tertiary)"
                    fontSize={"12px"}
                    fontWeight={500}
                  >
                    Account Setting
                  </CustomTypography>
                </MenuItem>

                {canSwitchBrand && (
                  <MenuItem
                    sx={{ color: "var(--text-primary)" }}
                    onClick={() => {
                      setSwitchBrandOpen(true);
                      setAnchorEl(null);
                      if (isMobile) {
                        setOpen(false);
                      }
                    }}
                  >
                    <CustomTypography
                      color="var(--text-primary)"
                      fontSize={"12px"}
                      fontWeight={500}
                    >
                      Switch Brand
                    </CustomTypography>
                  </MenuItem>
                )}

                {roles.find((x) => x.name === "Admin") ? (
                  <></>
                ) : (
                  <MenuItem
                    sx={{ color: "var(--text-primary)" }}
                    onClick={() => {
                      localStorage.setItem("path", `/oauth-clients`);
                      localStorage.setItem("selected_user", user?._id);
                      navigate(`/oauth-client`);
                      setAnchorEl(null);
                      if (isMobile) {
                        setOpen(false);
                      }
                    }}
                  >
                    <CustomTypography
                      color="var(--text-primary)"
                      fontSize={"12px"}
                      fontWeight={500}
                    >
                      Token
                    </CustomTypography>
                  </MenuItem>
                )}

                <MenuItem
                  sx={{ color: "var(--text-primary)" }}
                  onClick={() => {
                    // Simulate Alt + H key press
                    const event = new KeyboardEvent("keydown", {
                      key: "h",
                      altKey: true,
                      bubbles: true,
                    });
                    document.dispatchEvent(event);
                    handleMenuClose();
                  }}
                >
                  <CustomTypography
                    color="var(--text-primary)"
                    fontSize={"12px"}
                    fontWeight={500}
                  >
                    Help
                  </CustomTypography>
                </MenuItem>
                <MenuItem
                  sx={{ color: "var(--text-primary)" }}
                  onClick={logout}
                >
                  <CustomTypography
                    color="var(--text-primary)"
                    fontSize={"12px"}
                    fontWeight={500}
                  >
                    {hasAdminToken ? "Back to Admin View" : "Logout"}
                  </CustomTypography>
                </MenuItem>
                {hasAdminToken && (
                  <MenuItem
                    sx={{ color: "var(--text-primary)" }}
                    onClick={fullLogout}
                  >
                    <CustomTypography
                      color="var(--text-primary)"
                      fontSize={"12px"}
                      fontWeight={500}
                    >
                      Logout
                    </CustomTypography>
                  </MenuItem>
                )}
              </Menu>
            </Box>
          </Box>
        </Toolbar>
      </AppBar>
      <Box
        sx={{
          display: "flex",
          flexGrow: 1,
          marginTop: "60px",
          borderRight: "1px solid var(--border-color)",
        }}
      >
        <Drawer
          variant={isMobile ? "temporary" : "permanent"}
          open={open}
          onClose={toggleDrawer}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            width: isMobile ? 215 : drawerWidth,
            borderRight: isMobile ? "" : "1px solid var(--border-color)",
            flexShrink: 0,
            "& .MuiDrawer-paper": {
              width: isMobile ? 215 : drawerWidth,
              boxSizing: "border-box",
              borderRight: "1px solid var(--border-color)",
            },
          }}
        >
          {isMobile ? (
            <></>
          ) : (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexGrow: 1,
                marginLeft: isSidebarOpen ? 2 : 0,
                padding: 1,
                justifyContent: "space-between",
              }}
            >
              {!isSidebarOpen ? (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    padding: 10,
                    borderRadius: 100,
                    backgroundColor: "var(--text-tertiary)",
                    width: 40,
                    height: 40,
                    color: "#FFF",
                    cursor: "pointer",
                  }}
                >
                  <Typography
                    fontSize="14px"
                    fontWeight={600}
                    sx={{ userSelect: "none" }}
                    color={"var(--text-tertiary-dark)"}
                  >
                    {clientDetails?.business_name
                      ? clientDetails.business_name
                          .split(" ")
                          .map((word) => word[0].toUpperCase())
                          .join("")
                      : user.name.toString()[0]}
                  </Typography>
                </div>
              ) : clientDetails?.logo ? (
                <Box sx={{ position: "relative" }}>
                  <ResponsiveImage
                    style={{ borderRadius: 10, objectFit: "contain" }}
                    src={`${clientDetails?.logo}`}
                    alt="Logo"
                  />
                </Box>
              ) : clientDetails?.business_name ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.25,
                    px: 1.25,
                    py: 0.875,
                    borderRadius: "14px",
                    background: darkMode
                      ? "rgba(99,102,241,0.1)"
                      : "rgba(0,64,255,0.05)",
                    border: "1px solid",
                    borderColor: darkMode
                      ? "rgba(99,102,241,0.35)"
                      : "rgba(0,64,255,0.2)",
                    backdropFilter: "blur(6px)",
                    maxWidth: "155px",
                    overflow: "hidden",
                    cursor: "default",
                    height: 50,
                  }}
                >
                  {/* Business name */}
                  <CustomTypography
                    sx={{
                      background: darkMode
                        ? "linear-gradient(135deg, #818cf8 0%, #a5b4fc 100%)"
                        : "linear-gradient(135deg, #0040FF 0%, #6366f1 100%)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      backgroundClip: "text",
                      fontSize: "12px",
                      fontWeight: 700,
                      fontFamily: "Poppins",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      letterSpacing: "0.2px",
                      lineHeight: 1.3,
                    }}
                  >
                    {clientDetails.business_name}
                  </CustomTypography>
                </Box>
              ) : (
                <ResponsiveImage
                  style={{
                    cursor: "pointer",
                  }}
                  onClick={() => navigate("/")}
                  src="/DropSkipLogo.png"
                  alt="Logo"
                />
              )}
              {isSidebarOpen ? (
                <div
                  style={{
                    border: "1px solid var(--border-color)",
                    height: 35,
                    width: 35,
                    borderRadius: 10,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    setDrawerWidth(drawerWidth === 215 ? 80 : 215);
                    setOpen(!open);
                    setShowDrawer(!showDrawer);
                    setIsSidebarOpen(!isSidebarOpen);
                  }}
                >
                  <ChevronLeftIcon
                    sx={{
                      height: 25,
                      width: 25,
                      color: "var(--text-primary)",
                    }}
                  />
                </div>
              ) : (
                <></>
              )}
              {!isSidebarOpen ? (
                <div
                  style={{
                    height: 10,
                    width: 10,
                    borderRadius: 10,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "absolute",
                    right: 10,
                  }}
                  onClick={() => {
                    setDrawerWidth(drawerWidth === 215 ? 80 : 215);
                    setOpen(!open);
                    setShowDrawer(!showDrawer);
                    setIsSidebarOpen(!isSidebarOpen);
                  }}
                >
                  <ChevronRightIcon
                    sx={{
                      height: 25,
                      width: 25,
                      color: "#000",
                      cursor: "pointer",
                    }}
                  />
                </div>
              ) : (
                <></>
              )}
            </Box>
          )}
          <Box
            sx={{
              height: "100%",
              overflowY: "auto",
            }}
          >
            <Sidebar
              hideSideBar={isMobile ? false : !showDrawer}
              onClickMenu={toggleDrawer}
              onClickSubMenu={toggleDrawer}
            />
          </Box>
        </Drawer>

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            padding: ["/", "/admin-config"].includes(
              localStorage.getItem("path"),
            )
              ? 0
              : 2,
            width: { sm: `calc(100% - ${drawerWidth}px)` },
            backgroundColor: "var(--bg-default)",
            height: "90vh",
            overflowY: "auto",
            overflowX: "hidden",
            paddingTop: "0px",
          }}
        >
          {children}
        </Box>
      </Box>
      <Popover
        open={Boolean(anchorNotificationEl)}
        anchorEl={anchorNotificationEl}
        onClose={handleNotificationClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <NotificationModal
          onClose={handleNotificationClose}
          data={notifications}
          activeSection={activeSection}
          setActiveSection={(type) => {
            setActiveSection(type);
            setPage(0);
          }}
          notificationLoading={notificationLoading}
          loadMoreButton={loadMore}
          onClickLoadMore={() => {
            console.log(page);

            setPage((prevPage) => prevPage + 1);
          }}
        />
      </Popover>
      <AccountSettingsModal
        user={user}
        handleClose={() => setOpenAccountSettingModal(false)}
        open={openAccountSettingModal}
      ></AccountSettingsModal>
      <SwitchBrandModal
        open={switchBrandOpen}
        onClose={() => setSwitchBrandOpen(false)}
        clients={availableClients}
        currentClientId={client_id}
      />
      <ForecastResultModal />
      <ForecastParamsModal />
      <LostSalesParamsModal />
      <LostSalesResultModal />
    </Box>
  );
}
